import React, { useState, useEffect } from 'react'; // Импорт React хуков
import { Loader2, Plus, Trash2, LogOut, FileCheck, AlertCircle, ShoppingCart, Factory } from 'lucide-react'; // Иконки
import { Autocomplete } from './components/CreateOrder'; // Компонент автозаполнения
import { ProcessingOrders } from './components/ProcessingOrders'; // Компонент списка заказов
import { OrderDetails } from './components/OrderDetails'; // Компонент детального просмотра заказа
import { resolveOrderRefByNumber } from './services/OrderApi';
// Импорт API функций
import { getBOQs, searchNomenclature, searchProjects, searchSuppliers, submitOrder, searchOrganizations, getContracts } from './services/api';
// Импорт типов данных
import { BOQ, OrderRowItem, Project, Supplier, Product, Organization, Contract, ProcessingOrder } from './types';
// Импорт авторизации системы (отдельно от 1С)
import { validateSystemUser, setSystemSession, getSystemUser, clearSystemSession, touchSystemSession } from './services/systemAuth';

// Форматтер для денег (IDR - Индонезия)
const fmtMoney = new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' });

// Парсер денег (превращает "1,000.00" обратно в число)
const parseMoney = (s: string | number) => {
  if (typeof s === 'number') return s;
  return parseFloat(s.toString().replace(/\s/g, '').replace(',', '.')) || 0;
};

interface OrderPageRoute {
  orderNumber: string;
  orderDate?: string;
}

const getOrderPageFromHash = (): OrderPageRoute | null => {
  const match = window.location.hash.match(/^#\/orders\/([^/?#]+)(?:\?(.*))?$/);
  if (!match) return null;
  try {
    const query = new URLSearchParams(match[2] || '');
    return {
      orderNumber: decodeURIComponent(match[1]),
      orderDate: query.get('date') || undefined
    };
  } catch {
    return null;
  }
};

// --- Типы вкладок ---
type TabType = 'create' | 'processing';

interface AppTab {
    id: string; // Уникальный ID вкладки
    label: string; // Название вкладки
    type: TabType; // Тип вкладки
}

export default function App() {
  // Состояния авторизации
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [orderPage, setOrderPage] = useState<OrderPageRoute | null>(() => getOrderPageFromHash());
  const [resolvedOrderRef, setResolvedOrderRef] = useState<string | null>(null);
  const [isResolvingOrder, setIsResolvingOrder] = useState(false);
  const [orderResolveError, setOrderResolveError] = useState('');
  
  // --- Состояние системы вкладок ---
  const [tabs, setTabs] = useState<AppTab[]>([
      // { id: 'create', label: 'New Purchase Order', type: 'create' }, // Вкладка создания заказа — закомментировано
      { id: 'processing', label: 'Processing Orders', type: 'processing' } // Вкладка списка заказов
  ]);
  const [activeTabId, setActiveTabId] = useState('processing'); // ID активной вкладки (было 'create')

  // Состояния формы входа
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Данные приложения (Для формы создания заказа)
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  
  // Состояние контрактов
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [isLoadingContracts, setIsLoadingContracts] = useState(false);
  
  // Данные таблицы товаров
  const [boqList, setBoqList] = useState<BOQ[]>([]);
  const [rows, setRows] = useState<OrderRowItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Проверка авторизации в системе при загрузке страницы
  useEffect(() => {
    const user = getSystemUser();
    if (user) {
      setIsAuthenticated(true);
      loadInitialData();
    }
    setIsCheckingAuth(false);
  }, []);

  // URL вида /#/orders/{Number}?date={YYYY-MM-DD} открывает заказ по его номеру 1С.
  useEffect(() => {
    const updateOrderRoute = () => setOrderPage(getOrderPageFromHash());
    window.addEventListener('hashchange', updateOrderRoute);
    return () => window.removeEventListener('hashchange', updateOrderRoute);
  }, []);

  // Ссылка содержит номер 1С; дата отличает заказы с совпадающими номерами.
  useEffect(() => {
    let cancelled = false;
    setResolvedOrderRef(null);
    setOrderResolveError('');
    if (!orderPage || !isAuthenticated) {
      setIsResolvingOrder(false);
      return;
    }

    setIsResolvingOrder(true);
    resolveOrderRefByNumber(orderPage.orderNumber, orderPage.orderDate)
      .then((ref) => {
        if (cancelled) return;
        if (ref) setResolvedOrderRef(ref);
        else setOrderResolveError('Не удалось однозначно найти заказ по номеру и дате. Проверьте ссылку или откройте заказ из списка.');
      })
      .catch((error: unknown) => {
        if (!cancelled) setOrderResolveError(error instanceof Error ? error.message : 'Не удалось найти заказ в 1С.');
      })
      .finally(() => {
        if (!cancelled) setIsResolvingOrder(false);
      });

    return () => { cancelled = true; };
  }, [orderPage?.orderNumber, orderPage?.orderDate, isAuthenticated]);

  // Общая авторизация для вкладок браузера и завершение сессии после простоя.
  useEffect(() => {
    const syncSession = () => {
      const isSessionValid = Boolean(getSystemUser());
      setIsAuthenticated(isSessionValid);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'app_user' || event.key === 'app_last_activity' || event.key === null) syncSession();
    };
    const onActivity = () => {
      if (getSystemUser()) touchSystemSession();
      else setIsAuthenticated(false);
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('pointerdown', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('focus', onActivity);
    document.addEventListener('visibilitychange', onActivity);
    const idleCheck = window.setInterval(() => {
      if (!getSystemUser()) setIsAuthenticated(false);
    }, 60 * 1000);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('focus', onActivity);
      document.removeEventListener('visibilitychange', onActivity);
      window.clearInterval(idleCheck);
    };
  }, []);

  // Загрузка начальных данных (BOQ + первая пустая строка)
  const loadInitialData = () => {
    getBOQs().then(setBoqList).catch(console.error);
    setRows(prev => prev.length === 0 ? [createEmptyRow()] : prev);
  };

  // Эффект: Загрузка контрактов при выборе Организации и Поставщика
  useEffect(() => {
    if (organization && supplier) {
      setIsLoadingContracts(true);
      setContracts([]);
      setSelectedContract(null);

      getContracts(organization.Ref_Key, supplier.Ref_Key)
        .then((data) => {
          setContracts(data);
          // Если договор всего один, выбираем его автоматически
          if (data.length === 1) {
            setSelectedContract(data[0]);
          }
        })
        .catch(console.error)
        .finally(() => setIsLoadingContracts(false));
    } else {
      setContracts([]);
      setSelectedContract(null);
    }
  }, [organization, supplier]);

  // Обработчик входа в систему (проверка по данным из 1С: логин = Description, пароль = Значение)
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const user = username.trim();
    const pass = password.trim();

    if (!user || !pass) {
      setLoginError("Введите логин и пароль");
      return;
    }

    setIsLoggingIn(true);
    setLoginError('');

    const result = await validateSystemUser(user, pass);
    if (result.success) {
      setSystemSession(user, result.userRefKey);
      setIsAuthenticated(true);
      loadInitialData();
    } else {
      setLoginError(result.message);
    }
    setIsLoggingIn(false);
  };

  // Обработчик выхода
  const handleLogout = () => {
    clearSystemSession();
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setRows([]);
    setProject(null);
    setSupplier(null);
    setOrganization(null);
    setSelectedContract(null);
  };

  // --- Логика вкладок ---
  
  // Открытие страницы заказа в новой вкладке браузера
  const handleOpenOrderTab = (order: ProcessingOrder) => {
      const orderUrl = `${window.location.pathname}${window.location.search}#/orders/${encodeURIComponent(order.Number)}`;
      window.open(orderUrl, '_blank', 'noopener,noreferrer');
  };

  // Создание пустой строки товара
  const createEmptyRow = (): OrderRowItem => ({
    id: `row_${Date.now()}_${Math.random()}`,
    product: null,
    boqId: '',
    qty: 1.00,
    price: 0.00
  });

  const addRow = () => {
    setRows(prev => [...prev, createEmptyRow()]);
  };

  const removeRow = (id: string) => {
    setRows(prev => {
      const newRows = prev.filter(r => r.id !== id);
      return newRows.length === 0 ? [createEmptyRow()] : newRows;
    });
  };

  // Обновление поля в строке товара
  const updateRow = (id: string, field: keyof OrderRowItem, value: any) => {
    setRows(prev => prev.map(r => {
      if (r.id === id) {
        const updated = { ...r, [field]: value };
        // Если выбрали товар и у него есть цена, подставляем её
        if (field === 'product' && value) {
            const p = value as Product;
            if (p.Price) updated.price = p.Price;
        }
        return updated;
      }
      return r;
    }));
  };

  const calculateTotal = () => {
    return rows.reduce((acc, row) => acc + (row.qty * row.price), 0);
  };

  // Отправка формы создания заказа
  const handleSubmit = async () => {
    if (!organization || !project || !supplier) {
      alert("Please select Organization, Project, and Supplier.");
      return;
    }
    if (!selectedContract) {
      alert("Please select a Contract.");
      return;
    }
    const validItems = rows.filter(r => r.product !== null);
    if (validItems.length === 0) {
      alert("Please add at least one valid product.");
      return;
    }

    setIsSubmitting(true);
    const payload = {
      org: organization.Ref_Key,
      proj: project.Ref_Key,
      supp: supplier.Ref_Key,
      contract: selectedContract.Ref_Key,
      items: validItems.map(r => ({
        id: r.product?.Ref_Key,
        boq: r.boqId,
        q: r.qty,
        p: r.price
      }))
    };

    try {
      await submitOrder(payload);
      alert("Order successfully submitted!");
    } catch (e) {
      alert("Error submitting order.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Рендер ---

  // Экран загрузки (проверка токена)
  if (isCheckingAuth) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#f4f7f9]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0078d4]" />
      </div>
    );
  }

  // Экран входа
  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f4f7f9] p-4">
        <div className="w-full max-w-sm rounded-lg bg-white p-8 shadow-xl">
          <h2 className="mb-6 text-center text-2xl font-semibold text-[#0078d4]">Вход в систему</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Логин</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="user"
                className="w-full rounded border border-gray-300 bg-white p-3 text-sm focus:border-[#0078d4] focus:outline-none focus:ring-1 focus:ring-[#0078d4]"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Пароль</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••"
                className="w-full rounded border border-gray-300 bg-white p-3 text-sm focus:border-[#0078d4] focus:outline-none focus:ring-1 focus:ring-[#0078d4]"
              />
            </div>
            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full rounded bg-[#0078d4] p-3 text-sm font-bold text-white transition hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoggingIn ? 'Проверка...' : 'Войти'}
            </button>
            {loginError && (
              <div className="rounded bg-red-50 p-3 text-center text-xs text-red-600 border border-red-100">
                {loginError}
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  // Отдельная страница заказа; в ссылке показывается номер документа из 1С.
  if (orderPage) {
    return (
      <div className="min-h-screen w-full p-4 md:p-6">
        <div className="mx-auto w-full px-4 md:px-6">
          <div className="mb-6 flex items-center justify-between gap-4">
            <a
              href={`${window.location.pathname}${window.location.search}#/`}
              className="text-sm font-medium text-[#0078d4] hover:underline"
            >
              ← К списку заказов
            </a>
            <button
              onClick={handleLogout}
              className="rounded bg-gray-200 px-3 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-300"
            >
              Выйти
            </button>
          </div>
          {isResolvingOrder ? (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600">
              <Loader2 className="h-5 w-5 animate-spin text-[#0078d4]" />
              Поиск заказа {orderPage.orderNumber} в 1С…
            </div>
          ) : orderResolveError ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {orderResolveError}
            </div>
          ) : resolvedOrderRef ? (
            <OrderDetails orderRef={resolvedOrderRef} />
          ) : null}
        </div>
      </div>
    );
  }

  // Основной экран приложения
  return (
    <div className="min-h-screen w-full p-4 md:p-6">
      <div className="mx-auto w-full px-4 md:px-6">
        {/* Шапка с кнопкой выхода */}
        <div className="mb-6 flex justify-between items-center">
            <h1 className="text-xl font-bold text-gray-800">Procurement System</h1>
            <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500 hidden sm:block">Пользователь: <b>{getSystemUser() || '—'}</b></span>
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 rounded bg-gray-200 px-3 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-300"
                >
                    <LogOut className="h-3 w-3" />
                    Logout
                </button>
            </div>
        </div>

        {/* Панель вкладок */}
        <div className="mb-6 flex space-x-1 border-b border-gray-300 overflow-x-auto">
            {tabs.map((tab) => {
                const isActive = tab.id === activeTabId;
                const Icon = tab.type === 'create' ? ShoppingCart : Factory;
                
                return (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTabId(tab.id)}
                        className={`group flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors border-t border-l border-r min-w-fit ${
                            isActive
                            ? 'bg-white border-gray-300 text-[#0078d4] -mb-[1px] border-b-transparent' 
                            : 'bg-gray-100 text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        <Icon className="w-4 h-4" />
                        <span>{tab.label}</span>
                    </button>
                );
            })}
        </div>

        {/* Контент вкладок (Отрисовываем ВСЕ вкладки, но скрываем неактивные через CSS) */}
        {/* Это сохраняет состояние (введенные данные, загруженные списки) при переключении */}
        {tabs.map((tab) => (
           <div key={tab.id} style={{ display: tab.id === activeTabId ? 'block' : 'none' }}>
              
              {/* ВКЛАДКА СОЗДАНИЯ ЗАКАЗА — закомментировано */}
              {false && tab.type === 'create' && (
                <>
                  <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Autocomplete<Organization>
                      label="Organization"
                      placeholder="Search organization..."
                      onSearch={searchOrganizations}
                      onSelect={setOrganization}
                      displayValue={(item) => item.Description}
                      initialValue={organization}
                    />
                    <Autocomplete<Project>
                      label="Project"
                      placeholder="Search project..."
                      onSearch={searchProjects}
                      onSelect={setProject}
                      displayValue={(item) => item.Description}
                      initialValue={project}
                    />
                  </div>
                  
                  <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Autocomplete<Supplier>
                      label="Supplier"
                      placeholder="Search supplier..."
                      onSearch={searchSuppliers}
                      onSelect={setSupplier}
                      displayValue={(item) => item.Description}
                      initialValue={supplier}
                    />
                    
                    {/* Выбор договора */}
                    <div>
                      <label className="block mb-1 text-[11px] font-bold text-gray-500 uppercase">Contract</label>
                      {isLoadingContracts ? (
                          <div className="w-full p-2 border rounded border-gray-300 bg-gray-50 text-sm text-gray-500 flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Checking contracts...
                          </div>
                      ) : !organization || !supplier ? (
                          <div className="w-full p-2 border rounded border-gray-200 bg-gray-100 text-sm text-gray-400 italic">
                              Select Organization & Supplier first
                          </div>
                      ) : contracts.length === 0 ? (
                          <div className="w-full p-2 border rounded border-red-300 bg-red-50 text-sm text-red-600 flex items-center gap-2">
                              <AlertCircle className="h-4 w-4" />
                              No valid contracts found
                          </div>
                      ) : (
                          <select
                              className={`w-full p-2 border rounded text-sm focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4] transition-colors ${
                                  selectedContract ? 'bg-green-50 border-green-300 text-green-800' : 'bg-white border-gray-300'
                              }`}
                              value={selectedContract?.Ref_Key || ''}
                              onChange={(e) => {
                                  const c = contracts.find(c => c.Ref_Key === e.target.value);
                                  setSelectedContract(c || null);
                              }}
                          >
                              <option value="">-- Select Contract --</option>
                              {contracts.map(c => (
                                  <option key={c.Ref_Key} value={c.Ref_Key}>
                                      {c.Description} {c.НомерДоговора ? `(${c.НомерДоговора})` : ''}
                                  </option>
                              ))}
                          </select>
                      )}
                    </div>
                  </div>

                  {/* Таблица товаров */}
                  <div className="overflow-visible rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-left text-xs md:text-sm">
                        <thead>
                          <tr className="border-b-2 border-gray-200 bg-gray-50 text-gray-500">
                            <th className="w-[35%] p-3 font-semibold">Product (Search)</th>
                            <th className="w-[20%] p-3 font-semibold">BOQ</th>
                            <th className="w-[8%] p-3 font-semibold text-center">Unit</th>
                            <th className="w-[10%] p-3 font-semibold text-right">Qty</th>
                            <th className="w-[10%] p-3 font-semibold text-right">Price</th>
                            <th className="w-[12%] p-3 font-semibold text-right">Amount</th>
                            <th className="w-[5%] p-3 font-semibold"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => (
                            <tr key={row.id} className="group border-b border-gray-100 last:border-none hover:bg-blue-50/30">
                              <td className="p-2 align-top">
                                <Autocomplete<Product>
                                  placeholder="Type to search..."
                                  onSearch={searchNomenclature}
                                  onSelect={(item) => updateRow(row.id, 'product', item)}
                                  displayValue={(item) => item.Description}
                                  initialValue={row.product}
                                  className="w-full"
                                />
                              </td>
                              <td className="p-2 align-top">
                                <select
                                  className="w-full rounded border border-gray-300 bg-white p-2 text-xs focus:border-[#0078d4] focus:outline-none focus:ring-1 focus:ring-[#0078d4]"
                                  value={row.boqId}
                                  onChange={(e) => updateRow(row.id, 'boqId', e.target.value)}
                                >
                                  <option value="">-- Select BOQ --</option>
                                  {boqList.map(b => (
                                    <option key={b.Ref_Key} value={b.Ref_Key}>{b.Name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="p-2 text-center align-middle text-gray-500">
                                {row.product?.Unit || '-'}
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="w-full rounded border border-gray-300 bg-white p-2 text-right text-xs focus:border-[#0078d4] focus:outline-none focus:ring-1 focus:ring-[#0078d4]"
                                  value={row.qty}
                                  onChange={(e) => updateRow(row.id, 'qty', parseMoney(e.target.value))}
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="w-full rounded border border-gray-300 bg-white p-2 text-right text-xs focus:border-[#0078d4] focus:outline-none focus:ring-1 focus:ring-[#0078d4]"
                                  value={row.price}
                                  onChange={(e) => updateRow(row.id, 'price', parseMoney(e.target.value))}
                                />
                              </td>
                              <td className="p-2 text-right align-middle font-bold text-gray-700">
                                {fmtMoney.format(row.qty * row.price)}
                              </td>
                              <td className="p-2 text-center align-middle">
                                <button
                                  onClick={() => removeRow(row.id)}
                                  className="text-gray-400 transition hover:text-red-500"
                                  title="Remove Row"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                          
                          {rows.length === 0 && (
                            <tr>
                              <td colSpan={7} className="p-8 text-center text-gray-400">
                                Start by adding a row
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot className="bg-[#f0f7ff] font-bold text-[#0078d4]">
                          <tr>
                            <td colSpan={5} className="p-3 text-right">ORDER TOTAL:</td>
                            <td className="p-3 text-right">{fmtMoney.format(calculateTotal())}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    
                    <div className="border-t border-gray-100 p-3 bg-gray-50 rounded-b-lg">
                      <button
                        onClick={addRow}
                        className="flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      >
                        <Plus className="h-3 w-3" />
                        Add Product
                      </button>
                    </div>
                  </div>

                  {/* Футер: Кнопка отправки */}
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="flex items-center gap-2 rounded bg-[#0078d4] px-6 py-3 text-sm font-bold text-white shadow-md transition hover:bg-blue-600 disabled:opacity-70"
                    >
                      {isSubmitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <FileCheck className="h-4 w-4" />
                      )}
                      {isSubmitting ? 'Processing...' : 'Finalize Order'}
                    </button>
                  </div>
                </>
              )}

              {/* ВКЛАДКА СПИСКА ЗАКАЗОВ (PROCESSING) */}
              {tab.type === 'processing' && (
                <ProcessingOrders onOrderSelect={handleOpenOrderTab} />
              )}

           </div>
        ))}

      </div>
    </div>
  );
}
